import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { i18n } from 'src/common';
import { RadioOptionGroup, Card } from 'src/components';
import { CheckboxInput } from 'src/components/inputs/checkbox.input';
import { InputNumber } from 'src/components/inputs/inputNumber.input';
import { FieldConfig, Form, FormRef } from './form';
import { Validation } from './validation';

export interface HousePropertyInfoData {
  area?: number;
  exactYear?: number;
  before1964?: boolean;
  city?: string;
  cityKompareId?: string;
  cityParentName?: string;
  street?: string;
  streetKompareId?: string;
  floor?: string[];
  houseNumber?: string;
  masonry?: boolean;
  type?: 'apartment' | 'house' | string;
  usage?: 'permanent' | 'occasional' | 'rental' | string;
  zipCode?: number | string;
}

interface HousePropertyInfoProps {
  data: HousePropertyInfoData;
  onSubmit: (data: HousePropertyInfoData) => void;
}

export const HousePropertyInfoForm = forwardRef<FormRef, HousePropertyInfoProps>(
  ({ data, onSubmit }, ref) => {
    const formRef = React.useRef<FormRef>(null);

    const constructionOptions =[
      { value: true, label: i18n.t('components.forms.housePropertyInfo.constructionMasonry'), description: i18n.t('components.forms.housePropertyInfo.constructionMasonryDesc'), isPopular: true },
      { value: false, label: i18n.t('components.forms.housePropertyInfo.constructionMixed'), description: i18n.t('components.forms.housePropertyInfo.constructionMixedDesc'), isPopular: false },
    ];

    const usageOptions = useMemo(() => [
      { value: 'permanent', label: i18n.t('components.forms.housePropertyInfo.usagePermanent'), description: i18n.t('components.forms.housePropertyInfo.usagePermanentDesc'), isPopular: false },
      { value: 'temporary', label: i18n.t('components.forms.housePropertyInfo.usageTemporary'), description: i18n.t('components.forms.housePropertyInfo.usageTemporaryDesc'), isPopular: true },
      { value: 'longTermLease', label: i18n.t('components.forms.housePropertyInfo.usageLongTermLease'), description: i18n.t('components.forms.housePropertyInfo.usageLongTermLeaseDesc'), isPopular: false },
      { value: 'touristRental', label: i18n.t('components.forms.housePropertyInfo.usageTouristRental'), description: i18n.t('components.forms.housePropertyInfo.usageTouristRentalDesc'), isPopular: false },
    ], []);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (formRef.current) {
          formRef.current.submitForm();
        }
      },
      getValues: () => formRef.current?.getValues?.() || {},
      validateForm: () => formRef.current?.validateForm?.() ?? false,
      setFieldValue: (fieldName: string, value: unknown, validation?: boolean) => {
        formRef.current?.setFieldValue?.(fieldName, value, validation);
      },
    }));

    const fields: FieldConfig<HousePropertyInfoData>[] = useMemo(() => [
      {
        name: 'area',
        label: i18n.t('components.forms.housePropertyInfo.area'),
        component: InputNumber,
        validations: [{ type: 'required', message: i18n.t('components.forms.housePropertyInfo.areaRequired') }] as Validation[],
      },
      {
        name: 'masonry',
        label: i18n.t('components.forms.housePropertyInfo.constructionType'),
        component: RadioOptionGroup,
        options: constructionOptions,
        validations: [{ type: 'required', message: i18n.t('components.forms.housePropertyInfo.constructionTypeRequired') }] as Validation[],
      },
      {
        name: 'before1964',
        label: i18n.t('components.forms.housePropertyInfo.builtBefore1965'),
        component: CheckboxInput,
        componentProps: {
          left: true
        },
      },
      {
        name: 'usage',
        label: i18n.t('components.forms.housePropertyInfo.usage'),
        component: RadioOptionGroup,
        options: usageOptions,
        validations: [{ type: 'required', message: i18n.t('components.forms.housePropertyInfo.usageRequired') }] as Validation[],
      },
    ], [constructionOptions, usageOptions]);

    const initialValues: HousePropertyInfoData = {
      ...data,
      masonry: data.masonry,
      usage: data.usage as HousePropertyInfoData['usage'],
      before1964: data.before1964,
    };

    const handleSubmit = (values: HousePropertyInfoData) => {
      const convertedValues: HousePropertyInfoData = {
        ...values,
        area: typeof values.area === 'string' ? (values.area ? parseFloat(parseFloat(values.area).toFixed(2)) : undefined) : values.area,
        masonry: values.masonry,
        before1964: values.before1964 || false,
        usage: values.usage,
      } as HousePropertyInfoData;
      onSubmit(convertedValues);
    };

    return (
      <Card>
        <Form
          ref={formRef}
          fields={fields}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          config={{ gap: 32 }}
        />
      </Card>
    );
  }
);
